/* eslint-disable jsx-a11y/label-has-associated-control */
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import { useFormik, FormikProvider } from 'formik';
import saveIcon from '@iconify/icons-eva/save-outline';
import cameraIcon from '@iconify/icons-ic/baseline-camera-alt';
// material
import {
  Stack,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Badge,
  IconButton,
  Avatar
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { editItem, editItemExtension } from '../../fetch';
import { devicesActions } from '../../store';
import { timeForm, timeMs } from '../../common/utils/formatTime';
import { speedConverter, speedReverse } from '../../common/utils/unitConverter';
import { useTranslation } from '../../common/LocalizationProvider';
import {
  selectGroupSelectedId,
  selectGroupsArray,
  selectPositionsArray,
  selectSession
} from '../../createSelectorUtils';
// ----------------------------------------------------------------------

DeviceForm.propTypes = {
  deviceId: PropTypes.number,
  device: PropTypes.object
};

export default function DeviceForm({ deviceId, device }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const groups = useSelector(selectGroupsArray);

  const groupId = useSelector(selectGroupSelectedId);

  const position = useSelector(selectPositionsArray).find((item) => item.deviceId === deviceId);

  const session = useSelector(selectSession);

  const RegisterSchema = Yup.object().shape({
    device: Yup.object().shape({
      groupId: Yup.number(),
      name: Yup.string().required(t('yupValid_Required')),
      uniqueId: Yup.mixed().required(t('yupValid_Required'))
    })
  });

  const formik = useFormik({
    initialValues: {
      device,
      orometer:
        device !== undefined && position !== undefined
          ? timeForm(position.attributes.hours)
          : '0000:00'
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      const responseDevice = await editItem('devices', deviceId, values.device);
      if (responseDevice.ok) {
        if (
          device !== undefined &&
          values.orometer !== timeForm(position === undefined ? 0 : position.attributes.hours)
        ) {
          await editItemExtension('devices', deviceId, 'accumulators', {
            deviceId: values.device.id,
            hours: timeMs(values.orometer)
          });
        }
        enqueueSnackbar(device !== undefined ? t('DL_Snackbar_Change') : t('DL_Snackbar_Add'), {
          variant: 'success'
        });
        dispatch(devicesActions.update([await responseDevice.json()]));
        navigate('/device', { replace: true });
      } else {
        const error = await responseDevice.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
        setSubmitting(false);
      }
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  const handleChangeImageFile = (event) => {
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);

    reader.onload = (e) => {
      setFieldValue('device.image', e.target.result);
    };
  };
  return (
    <FormikProvider value={formik}>
      {device !== undefined && (
        <Stack spacing={0}>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 4 }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              badgeContent={
                <>
                  <input
                    accept="image/*"
                    id="icon-button-file"
                    hidden
                    type="file"
                    onChange={handleChangeImageFile}
                  />
                  <label htmlFor="icon-button-file">
                    <IconButton
                      sx={{
                        color: 'black',
                        backgroundColor: '#D3D3D3'
                      }}
                      aria-label="upload picture"
                      component="span"
                    >
                      <Icon icon={cameraIcon} width={30} height={30} />
                    </IconButton>
                  </label>
                </>
              }
            >
              <Avatar
                alt="DeviceImage"
                sx={{ width: 200, height: 200 }}
                src={
                  getFieldProps('device.image').value === null
                    ? '/static/illustrations/illustration-plane.svg'
                    : getFieldProps('device.image').value
                }
              />
            </Badge>
          </Stack>
          {session.administrator && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ my: 4 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    {...getFieldProps('device.disabled')}
                    color="primary"
                    checked={getFieldProps('device.disabled').value === true}
                  />
                }
                label={t('DL_Form_Disabled')}
                labelPlacement="start"
              />
            </Stack>
          )}
        </Stack>
      )}
      <Stack spacing={3}>
        {session.administrator && (
          <Autocomplete
            fullWidth
            {...getFieldProps('device.groupId')}
            options={groups}
            getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
            value={
              groups.find((group) => group.id === getFieldProps('device.groupId').value) || null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('fields_Club')}
                error={Boolean(touched.club && errors.club)}
                helperText={touched.club && errors.club}
              />
            )}
            onChange={(event, value) =>
              setFieldValue('device.groupId', value !== null ? value.id : 0)
            }
          />
        )}
        <TextField
          fullWidth
          label={t('fields_Name_Device')}
          {...getFieldProps('device.name')}
          value={getFieldProps('device.name').value || ''}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
          InputProps={{
            readOnly: !(session.administrator || groupId === 0)
          }}
        />

        {(session.administrator ||
          device === undefined ||
          (groupId === 0 && device !== undefined && device.groupId === 0)) && (
          <TextField
            fullWidth
            label={t('fields_Indicative')}
            {...getFieldProps('device.uniqueId')}
            value={getFieldProps('device.uniqueId').value || ''}
            error={Boolean(touched.uniqueId && errors.uniqueId)}
            helperText={touched.uniqueId && errors.uniqueId}
          />
        )}

        {(session.administrator || groupId === 0) && (
          <TextField
            fullWidth
            label={t('fields_Phone')}
            type="number"
            autoComplete="telephone"
            {...getFieldProps('device.phone')}
            value={getFieldProps('device.phone').value || ''}
          />
        )}

        {device !== undefined && (
          <>
            <TextField
              fullWidth
              label={t('fields_Orometer')}
              autoComplete="orometer"
              {...getFieldProps('orometer')}
              value={getFieldProps('orometer').value || ''}
            />

            <Divider />
            <Typography variant="h6" gutterBottom>
              {t('DL_Form_ParamRoulage')}
            </Typography>
            <TextField
              fullWidth
              type="number"
              autoComplete="maxSpeed"
              label={`Max speed (${session.uniteVitesse})`}
              defaultValue={
                speedConverter(
                  getFieldProps('device.speedMaxRoulage').value,
                  session.uniteVitesse
                ) || ''
              }
              onChange={(event) =>
                setFieldValue(
                  'device.speedMaxRoulage',
                  speedReverse(event.target.value, session.uniteVitesse)
                )
              }
            />
            <TextField
              fullWidth
              type="number"
              autoComplete="gAlert"
              label="G alert"
              {...getFieldProps('device.gRoulageMinAlert')}
              value={getFieldProps('device.gRoulageMinAlert').value || ''}
            />

            <TextField
              fullWidth
              type="number"
              autoComplete="gWarning"
              label="G warning"
              {...getFieldProps('device.gRoulageMinWarning')}
              value={getFieldProps('device.gRoulageMinWarning').value || ''}
            />
            <Divider />
            <Typography variant="h6" gutterBottom>
              {t('DL_Form_ParamDecollage')}
            </Typography>

            <TextField
              fullWidth
              type="number"
              autoComplete="speedMinMontee"
              label={`Speed min take off (${session.uniteVitesse})`}
              defaultValue={
                speedConverter(
                  getFieldProps('device.speedMinMontee').value,
                  session.uniteVitesse
                ) || ''
              }
              onChange={(event) =>
                setFieldValue(
                  'device.speedMinMontee',
                  speedReverse(event.target.value, session.uniteVitesse)
                )
              }
            />
            <Divider />
            <Typography variant="h6" gutterBottom>
              {t('DL_Form_ParamFlight')}
            </Typography>

            <TextField
              fullWidth
              type="number"
              autoComplete="vne"
              label={`Vne (${session.uniteVitesse})`}
              defaultValue={
                speedConverter(getFieldProps('device.vne').value, session.uniteVitesse) || ''
              }
              onChange={(event) =>
                setFieldValue('device.vne', speedReverse(event.target.value, session.uniteVitesse))
              }
            />

            <TextField
              fullWidth
              type="number"
              autoComplete="gAlert"
              label="G alert"
              {...getFieldProps('device.gVolMinAlert')}
              value={getFieldProps('device.gVolMinAlert').value || ''}
            />
            <Divider />
            <Typography variant="h6" gutterBottom>
              {t('DL_Form_ParamLanding')}
            </Typography>

            <TextField
              fullWidth
              type="number"
              autoComplete="speed"
              label={`Speed min alert (${session.uniteVitesse})`}
              defaultValue={
                speedConverter(
                  getFieldProps('device.speedMaxAtterrissageAlert').value,
                  session.uniteVitesse
                ) || ''
              }
              onChange={(event) =>
                setFieldValue(
                  'device.speedMaxAtterrissageAlert',
                  speedReverse(event.target.value, session.uniteVitesse)
                )
              }
            />
            <TextField
              fullWidth
              type="number"
              autoComplete="Vne"
              label={`Speed min warning (${session.uniteVitesse})`}
              defaultValue={
                speedConverter(
                  getFieldProps('device.speedMaxAtterrissageWarning').value,
                  session.uniteVitesse
                ) || ''
              }
              onChange={(event) =>
                setFieldValue(
                  'device.speedMaxAtterrissageWarning',
                  speedReverse(event.target.value, session.uniteVitesse)
                )
              }
            />

            <TextField
              fullWidth
              type="number"
              autoComplete="gAlert"
              label="G alert"
              {...getFieldProps('device.gAtterrissageMinAlert')}
              value={getFieldProps('device.gAtterrissageMinAlert').value || ''}
            />

            <TextField
              fullWidth
              type="number"
              autoComplete="gWarning"
              label="G warning"
              {...getFieldProps('device.gAtterrissageMinWarning')}
              value={getFieldProps('device.gAtterrissageMinWarning').value || ''}
            />
          </>
        )}

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          startIcon={<Icon icon={saveIcon} />}
          onClick={handleSubmit}
        >
          {t('btn_Save')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
